import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [settingsData, setSettingsData] = useState(null);

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const fetchSettingsData = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getAdminSettings();
      if (response.success) {
        setSettingsData(response);
      } else {
        toast.error(response.message || 'Failed to fetch settings data');
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
      toast.error('Failed to fetch settings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-600 mt-2">Manage system configuration and settings</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {/* API Manager Card */}
        <Link 
          to="/admin/dashboard/settings/api-manager" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-blue-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">API Manager</h3>
              <p className="text-gray-600 text-sm">Configure and manage API endpoints</p>
            </div>
          </div>
          {settingsData?.apiStatus && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">APIs Configured: </span>
              {settingsData.apiStatus.apiInfo?.length || 0}
            </div>
          )}
        </Link>

        {/* Platform Settings Card */}
        <Link 
          to="/admin/dashboard/settings/platform" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-green-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Platform Fee & GST Settings</h3>
              <p className="text-gray-600 text-sm">Configure platform fees and GST rates</p>
            </div>
          </div>
          {settingsData?.platformSettings && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Platform Fee: </span>
              ₹{settingsData.platformSettings.platformFee || 0} | 
              <span className="font-medium ml-2">GST: </span>
              {settingsData.platformSettings.gstRate || 0}%
            </div>
          )}
        </Link>

        {/* Cancellation Policy Card */}
        <Link 
          to="/admin/dashboard/settings/cancellation-policy" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-orange-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-orange-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancellation Policy Settings</h3>
              <p className="text-gray-600 text-sm">Configure cancellation time limits</p>
            </div>
          </div>
          {settingsData?.cancellationSettings && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Cancellation Offset: </span>
              {settingsData.cancellationSettings.cancellationHoursOffset || 0} hours
            </div>
          )}
        </Link>

        {/* Add Support Executive Card */}
        <Link 
          to="/admin/dashboard/support-manager" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-purple-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-purple-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Support Management</h3>
              <p className="text-gray-600 text-sm">Manage support executives and staff</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            <span className="font-medium">Manage support team members</span>
          </div>
        </Link>
      </div>

      {/* Quick Stats Section */}
      {settingsData && (
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {settingsData.apiStatus?.apiInfo?.length || 0}
              </div>
              <div className="text-sm text-blue-800">Configured APIs</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                ₹{settingsData.platformSettings?.platformFee || 0}
              </div>
              <div className="text-sm text-green-800">Platform Fee</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {settingsData.cancellationSettings?.cancellationHoursOffset || 0}h
              </div>
              <div className="text-sm text-orange-800">Cancellation Window</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;